import type {
  NextPayoutResponseType,
  PayoutListResponseType,
} from "@sellar-io/api-v2/types";
import { PaginationState } from "@tanstack/react-table";
import { useMemo, useState } from "react";
import { twMerge } from "tailwind-merge";

import { SpinnerLegacy } from "../../../../components";
import { usePayoutPaginatedList } from "../../../../hooks/api/v2/payout/usePayoutList";
import dayjs from "dayjs";
import { capitalize, formatCurrency, formatDateRange } from "@sellar-io/common";
import {
  BadgeGreen,
  BadgeWhiteFill,
  BadgeRed,
  BadgeZinc,
} from "../../../../components/badge/Badge";
import {
  InputContainer,
  createSellarColumnHelper,
} from "@sellar-io/components";
import { SellarCreditsFilter } from "./SellarCreditFilter";
import { FilterPopover } from "../../../../components/filterPopover/FilterPopover";
import { useSellarCreditFilterStore } from "../stores/SellarCreditFilterStore";
import { useShallow } from "zustand/shallow";
import { useDebounce } from "use-debounce";
import { SELLAR_BUSINESS_ID } from "../../../../constants/sellarConstants.shared";
import { Tag } from "../../../../components/tag/Tag";
import { SellarCreditStats } from "./SellarCreditStats";
import { InputText, TablePaginated } from "@sellar-io/components";
import { Icon } from "@sellar-io/components";
import { getLastFriday, getEmptyMessage } from "./SellarCreditTable.shared";

type ColumnDataType = PayoutListResponseType[number];

export const SellarCreditTable = ({
  setSelectedPayout,
  setActive,
}: {
  setSelectedPayout: (
    payout: PayoutListResponseType[number] | NextPayoutResponseType,
  ) => void;
  setActive: (value: boolean) => void;
}) => {
  const [search, setSearch] = useState("");
  const [desktopFilter, setDesktopFilter] = useState(false);
  const [sorting, setSorting] = useState<{ id: string; desc: boolean }[]>([
    { id: "createdAt", desc: true },
  ]);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 25,
  });

  const [debouncedSearchValue] = useDebounce(search, 500);

  const state = useSellarCreditFilterStore(useShallow((state) => state));

  const payoutQuery = usePayoutPaginatedList({
    limit: pagination.pageSize,
    offset: pagination.pageIndex * pagination.pageSize,
    search: debouncedSearchValue,
    status: state.status && state.status !== "all" ? state.status : undefined,
    sortBy: (sorting[0]?.id as "createdAt" | "amount") || undefined,
    sortDir: sorting[0]?.desc ? "desc" : "asc",
  });
  const { data } = payoutQuery;
  const flatData = useMemo(() => data?.rows.map((page) => page) ?? [], [data]);
  const count = data?.count ?? 0;

  const pageCount = Math.ceil(count / pagination.pageSize);

  const handleTableSort = (columnSort: string | null) => {
    const [id, dir] = columnSort?.split("_") || [];
    const desc = dir === "desc";

    if (id && (sorting[0]?.id !== id || sorting[0].desc !== desc)) {
      setSorting([{ id, desc }]);
    }
  };

  const columnHelper = createSellarColumnHelper<ColumnDataType>();

  const columns = [
    columnHelper.accessor("id", {
      id: "id",
      header: "Payout #",
      enableSorting: false,
      cell: (info) => info.getValue(),
    }),
    columnHelper.accessor("createdAt", {
      id: "createdAt",
      header: "Date",
      enableSorting: true,
      cell: (info) => {
        const createdDate = info.getValue();
        return createdDate
          ? formatDateRange(getLastFriday(createdDate).toDate(), 6)
          : "NA";
      },
    }),
    columnHelper.accessor("amount", {
      id: "amount",
      header: "Amount (gross)",
      enableSorting: true,
      cell: (info) => {
        const row = info.row.original;
        const fee =
          row.Invoices.find(
            (invoice) =>
              invoice.type === "sellarCreditSupplier" &&
              invoice.SenderBusinessId === SELLAR_BUSINESS_ID,
          )?.total || 0;
        return formatCurrency(info.getValue() + fee);
      },
    }),
    columnHelper.accessor("Invoices", {
      id: "fee",
      header: "Fees",
      enableSorting: false,
      cell: (info) => {
        const row = info.row.original;
        const fee =
          row.Invoices.find(
            (invoice) =>
              invoice.type === "sellarCreditSupplier" &&
              invoice.SenderBusinessId === SELLAR_BUSINESS_ID,
          )?.total || 0;
        return formatCurrency(fee);
      },
    }),
    columnHelper.accessor("amount", {
      id: "net",
      header: "Total (net)",
      enableSorting: false,
      cell: (info) => formatCurrency(info.getValue()),
    }),
    columnHelper.accessor((row) => row, {
      id: "status",
      header: "Status",
      enableSorting: false,
      cell: (info) => {
        const row = info.getValue();
        switch (row.status) {
          case "draft":
            return (
              <BadgeWhiteFill className="text-sm font-medium">
                Draft
              </BadgeWhiteFill>
            );
          case "payment_processing":
            return (
              <BadgeZinc className="text-sm font-medium">
                Payment&nbsp;processing
              </BadgeZinc>
            );
          case "paid":
            return (
              <BadgeGreen className="text-sm font-medium">
                Paid:&nbsp;{dayjs(row.payoutDate).format("DD/MM/YYYY")}
              </BadgeGreen>
            );
          case "failed":
            return <BadgeRed className="text-sm font-medium">Failed</BadgeRed>;
          default:
            return row.status;
        }
      },
    }),
    columnHelper.accessor((row) => row, {
      id: "action",
      header: "",
      enableSorting: false,
      cell: () => (
        <div className="flex w-full justify-end">
          <Icon icon="CHEVRON_RIGHT" size={1} />
        </div>
      ),
    }),
  ];

  if (payoutQuery.isLoading && flatData.length === 0) {
    return <SpinnerLegacy />;
  }

  return (
    <>
      <SellarCreditStats
        setSelectedPayout={setSelectedPayout}
        setActive={setActive}
      />
      <div className="container mx-auto mt-6 max-w-[1300px]">
        {/* Table header with search and filter */}
        <div
          className={twMerge(
            "flex w-full border-collapse flex-wrap items-center justify-between rounded-t-lg border border-b-0 border-zinc-300 bg-zinc-50 p-4",
          )}
        >
          <div className="flex items-center">
            {/* TODO add filters and search */}
            <InputContainer className="mr-2 w-full min-w-[300px] py-[0.4rem]">
              <InputText
                required={false}
                placeholder="Search PAY #..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full"
              />
            </InputContainer>
            <FilterPopover
              icon="FILTER_VARIANT"
              buttonText="Filter"
              compact={false}
              active={desktopFilter}
              toggleActive={(value: boolean) => setDesktopFilter(value)}
            >
              <SellarCreditsFilter
                onDismiss={() => {
                  setDesktopFilter(false);
                }}
              />
            </FilterPopover>
          </div>

          {state.status && state.status !== "all" && (
            <div className="flex w-full flex-wrap items-center gap-2 pt-2">
              <Tag
                className="flex items-center gap-1"
                onClick={() => state.setStatus("all")}
              >
                {capitalize(state.status.replace(/_/g, " "))}
              </Tag>
            </div>
          )}
        </div>
        <TablePaginated
          columns={columns}
          data={flatData}
          pageCount={pageCount}
          pagination={pagination}
          setPagination={setPagination}
          columnSortName={
            sorting.length
              ? `${sorting[0]?.id}_${sorting[0]?.desc ? "desc" : "asc"}`
              : undefined
          }
          handleTableSort={handleTableSort}
          onRowClick={(row) => {
            setSelectedPayout(row);
            setActive(true);
          }}
          noResultsText={getEmptyMessage(search, state.status)}
          paginationLabel="Rows per page"
          gridClasses="grid-cols-[repeat(6,_1fr)_40px]"
        />
      </div>
    </>
  );
};
